import React, { useMemo, useState } from 'react';
import moment from 'moment';
import {
  CareType,
  ContractWeek,
  ContractWeeks,
  EditContractWeekIntakesInput,
  EditContractWeekSessionsInput,
  useUpdateContractWeeksMutation,
} from '../../../graphQL';
import { FinalButton } from '../../../Components/FinalButton';
import { useCareTypeConfigContext } from '../../../Components/CareTypeConfigContext';
import { pastColumnCopy, thisWeekColumnCopy, upcomingColumnCopy } from './ContractUtilizationCopy';
import { ContractUtilizationRow, ContractUtilizationTable } from './ContractUtilizationTable';
import {
  PairedFieldValidationErrors,
  updatePairedFieldValidationErrors,
} from '../../../utils/pairedFieldValidation';

type UtilizationDashboardProps = {
  careType: CareType;
  editable?: boolean;
  // Only the records for this careType
  utilizationRecords: ContractWeeks;
  refetch: () => void | Promise<void>;
};

// The server version of contract week input is composed differently (EditContractWeekIntakesInput, EditContractWeekSessionsInput)
// But this ContractUtilizationTable component still has each week combined.
type ContractWeekInput = {
  id: number;
  sessions?: number | null;
  intakes?: number | null;
};

type ContractWeekValueKey = keyof Pick<ContractWeekInput, 'sessions' | 'intakes'>;

const contractWeekValueKeyByInputType: Record<'session' | 'intake', ContractWeekValueKey> = {
  session: 'sessions',
  intake: 'intakes',
};

const pairedContractWeekValueKeys: Record<ContractWeekValueKey, ContractWeekValueKey> = {
  sessions: 'intakes',
  intakes: 'sessions',
};

type UpdateMap = {
  [key: number]: ContractWeekInput;
};

type ContractWeekValues = {
  [key in ContractWeekValueKey]?: number | null;
};

type ValidationErrors = PairedFieldValidationErrors;

type EditState = {
  contractWeeks: UpdateMap;
  errors: ValidationErrors;
};

export const ContractUtilizationDashboard = ({
  careType,
  editable,
  utilizationRecords,
  refetch,
}: UtilizationDashboardProps) => {
  const { getCareTypeDisplay } = useCareTypeConfigContext();
  const serviceTitle = getCareTypeDisplay(careType).serviceTitle;
  const [isEditable, setIsEditable] = useState(false);
  const [update] = useUpdateContractWeeksMutation();
  const [editState, setEditState] = useState<EditState>({ contractWeeks: {}, errors: {} });
  const hasValidationErrors = Object.values(editState.errors).some(Boolean);

  const {
    past: completedRows,
    current: currentRows,
    upcoming: upcomingRows,
  } = useMemo(() => {
    return prepUtilizationRows(utilizationRecords);
  }, [utilizationRecords]);

  const getContractWeekError = (isIntake: boolean, contractWeekId: number): string | undefined => {
    const valueKey = getContractWeekValueKey(isIntake);
    return editState.errors[getValidationErrorKey(contractWeekId, valueKey)];
  };

  const handleChange = (newValue: string, isIntake: boolean, contractWeekId: number) => {
    const valueKey = getContractWeekValueKey(isIntake);

    setEditState(currentState => {
      const contractWeeks = updateContractWeek(
        currentState.contractWeeks,
        contractWeekId,
        valueKey,
        getNumFromVal(newValue, isIntake)
      );

      return {
        contractWeeks,
        errors: updateValidationErrors({
          currentErrors: currentState.errors,
          newValue,
          valueKey,
          contractWeek: contractWeeks[contractWeekId],
          contractWeekId,
          utilizationRecords,
        }),
      };
    });
  };

  // TODO: Handle errors from api (note this is a old DGM todo, not a new sessions todo)
  const onSave = async () => {
    if (!Object.keys(editState.contractWeeks).length) {
      return;
    }
    const weekIntakes: EditContractWeekIntakesInput[] = [];
    const weekSessions: EditContractWeekSessionsInput[] = [];

    Object.values(editState.contractWeeks).forEach(contractWeek => {
      if (contractWeek.intakes != null) {
        weekIntakes.push({
          id: contractWeek.id,
          intakes: contractWeek.intakes,
        });
      }
      if (contractWeek.sessions != null) {
        weekSessions.push({
          id: contractWeek.id,
          sessions: contractWeek.sessions,
        });
      }
    });

    await update({
      variables: {
        input: {
          careTypes: [
            {
              careType,
              weekIntakes,
              weekSessions,
            },
          ],
        },
      },
    });
    await refetch();
    setEditState({ contractWeeks: {}, errors: {} });
  };

  // TODO: handle attempts to cancel or change tabs with unsaved changed (note this is a old DGM todo, not a new sessions todo)
  return (
    <div>
      {completedRows.length > 0 && (
        <div className="mb4">
          {/* <CompletedUtilizationTable records={utilizationRecords.past} editable={false} /> */}
          <ContractUtilizationTable
            heading="Completed"
            rows={completedRows}
            columns={pastColumnCopy(serviceTitle)}
            editable={false}
          />
        </div>
      )}
      {currentRows.length > 0 && (
        <div className="mb4">
          <ContractUtilizationTable
            heading="This Week"
            rows={currentRows}
            columns={thisWeekColumnCopy(serviceTitle)}
            editable={isEditable}
            // Columns for the current week are (Max Sessions - EDITABLE, Sessions Utilized, Sessions Scheduled, Max Intakes - EDITABLE, Intakes Utilized, Intakes Scheduled)
            editableColumns={[[false], [true, false, false], [true, false, false]]}
            handleChange={handleChange}
            getError={getContractWeekError}
          />
        </div>
      )}
      {upcomingRows.length > 0 && (
        <div className="mb4">
          <ContractUtilizationTable
            heading="Upcoming"
            rows={upcomingRows}
            columns={upcomingColumnCopy(serviceTitle)}
            editable={isEditable}
            // Columns for the current week are (Max Sessions - EDITABLE, Sessions Scheduled, Max Intakes - EDITABLE, Intakes Scheduled)
            editableColumns={[[false], [true, false], [true, false]]}
            handleChange={handleChange}
            getError={getContractWeekError}
          />
        </div>
      )}

      {editable && (
        <div>
          {!isEditable ? (
            <FinalButton
              kind="outline_black"
              onClick={() => {
                setIsEditable(true);
              }}
            >
              Edit {serviceTitle} Sessions
            </FinalButton>
          ) : (
            <>
              <FinalButton
                kind="primary"
                className="mr2"
                disabled={hasValidationErrors}
                onClick={async () => {
                  await onSave();
                  setIsEditable(false);
                }}
              >
                Save Changes
              </FinalButton>
              <FinalButton
                kind="minimal_gray"
                onClick={() => {
                  setIsEditable(false);
                }}
              >
                Cancel
              </FinalButton>
            </>
          )}
        </div>
      )}
    </div>
  );
};

function validateAllocationValue(
  newValue: string,
  valueKey: ContractWeekValueKey,
  comparator: number | null | undefined
) {
  // Allow users to clear out values
  if (newValue === '') {
    return { isValid: true, errorString: '' };
  }

  const isIntake = isIntakeContractWeekValueKey(valueKey);
  const newValNum = getNumFromVal(newValue, isIntake);

  if (isIntake) {
    // Validation for Intake Fields
    // First check that it's a whole number
    const intakeRegex = /^\d+$/;
    if (newValue.match(intakeRegex) === null) {
      return { isValid: false, errorString: 'Only positive whole numbers allowed.' };
    }

    // Then, if have a comparator, compare, otherwise valid
    if ((comparator === 0 || comparator) && newValNum > comparator) {
      return {
        isValid: false,
        errorString: 'Number of max intakes cannot exceed max bookable sessions.',
      };
    }
  } else {
    // Validation for Sessions Fields (Non Intake)
    // First check that it's a whole number or 0.25, .5, .75, .0
    // First check that it's a whole number
    const sessionsRegex = /^\d+$/;
    if (newValue.match(sessionsRegex) === null) {
      return { isValid: false, errorString: 'Only positive whole numbers allowed.' };
    }
    // Then, if have a comparator, compare, otherwise valid
    if ((comparator === 0 || comparator) && comparator > newValNum) {
      return {
        isValid: false,
        errorString: 'Number of max bookable sessions cannot be less than max intakes',
      };
    }
  }
  return { isValid: true, errorString: '' };
}

function updateContractWeek(
  currentState: UpdateMap,
  contractWeekId: number,
  valueKey: ContractWeekValueKey,
  value: number | null
): UpdateMap {
  return {
    ...currentState,
    [contractWeekId]: {
      ...(currentState[contractWeekId] ?? { id: contractWeekId }),
      [valueKey]: value,
    },
  };
}

function updateValidationErrors({
  currentErrors,
  newValue,
  valueKey,
  contractWeek,
  contractWeekId,
  utilizationRecords,
}: {
  currentErrors: ValidationErrors;
  newValue: string;
  valueKey: ContractWeekValueKey;
  contractWeek: ContractWeekInput;
  contractWeekId: number;
  utilizationRecords: ContractWeeks;
}): ValidationErrors {
  const values = getContractWeekValues({
    contractWeek,
    contractWeekId,
    utilizationRecords,
  });

  return updatePairedFieldValidationErrors({
    currentErrors,
    newValue,
    valueKey,
    values,
    getPairedValueKey: getPairedContractWeekValueKey,
    getValidationErrorKey: currentValueKey =>
      getValidationErrorKey(contractWeekId, currentValueKey),
    validateValue: validateAllocationValue,
  });
}

function getContractWeekValues({
  contractWeek,
  contractWeekId,
  utilizationRecords,
}: {
  contractWeek: ContractWeekInput;
  contractWeekId: number;
  utilizationRecords: ContractWeeks;
}): ContractWeekValues {
  const storedWeek = [utilizationRecords.current, ...utilizationRecords.upcoming].find(
    week => week?.id === contractWeekId
  );

  return {
    sessions: 'sessions' in contractWeek ? contractWeek.sessions : storedWeek?.sessionsMax,
    intakes: 'intakes' in contractWeek ? contractWeek.intakes : storedWeek?.intakesMax,
  };
}

function getContractWeekValueKey(isIntake: boolean): ContractWeekValueKey {
  return isIntake
    ? contractWeekValueKeyByInputType.intake
    : contractWeekValueKeyByInputType.session;
}

function getPairedContractWeekValueKey(valueKey: ContractWeekValueKey): ContractWeekValueKey {
  return pairedContractWeekValueKeys[valueKey];
}

function isIntakeContractWeekValueKey(valueKey: ContractWeekValueKey): boolean {
  return valueKey === contractWeekValueKeyByInputType.intake;
}

function getValidationErrorKey(contractWeekId: number, valueKey: ContractWeekValueKey) {
  return `${contractWeekId}-${valueKey}`;
}

type PreppedRows = {
  past: ContractUtilizationRow[];
  current: ContractUtilizationRow[];
  upcoming: ContractUtilizationRow[];
};

function prepUtilizationRows(utilizationRecords: ContractWeeks): PreppedRows {
  const result: PreppedRows = { past: [], current: [], upcoming: [] };
  // Completed weeks in contract
  for (const record of utilizationRecords.past) {
    const rowWeekText = prepWeekText(record);
    result.past.push({
      id: record.id,
      rowData: [
        [rowWeekText],
        [
          record.sessionsMax.toLocaleString(),
          record.sessionsUtilized.toLocaleString(),
          record.intakesUtilized.toLocaleString(),
        ],
      ],
    });
  }
  // Current week in contract
  if (utilizationRecords.current) {
    result.current.push({
      id: utilizationRecords.current.id,
      rowData: [
        [prepWeekText(utilizationRecords.current)],
        [
          utilizationRecords.current.sessionsMax.toLocaleString(),
          utilizationRecords.current.sessionsUtilized.toLocaleString(),
          utilizationRecords.current.sessionsScheduled.toLocaleString(),
        ],
        [
          utilizationRecords.current.intakesMax.toLocaleString(),
          utilizationRecords.current.intakesUtilized.toLocaleString(),
          utilizationRecords.current.intakesScheduled.toLocaleString(),
        ],
      ],
    });
  }

  // Upcoming weeks in contract
  for (const record of utilizationRecords.upcoming) {
    const rowWeekText = prepWeekText(record);
    result.upcoming.push({
      id: record.id,
      rowData: [
        [rowWeekText],
        [record.sessionsMax.toLocaleString(), record.sessionsScheduled.toLocaleString()],
        [record.intakesMax.toLocaleString(), record.intakesScheduled.toLocaleString()],
      ],
    });
  }

  result.upcoming.sort((session1, session2) => session1.id - session2.id);

  return result;
}

function prepWeekText(contractWeek: ContractWeek) {
  const mmtStart = moment(contractWeek.startDate);
  const mmtEnd = moment(contractWeek.endDate);
  const diff = mmtEnd.diff(mmtStart, 'days') + 1;

  let rowWeekText = `${moment(contractWeek.startDate).format('M/D/YYYY')} - ${moment(
    contractWeek.endDate
  ).format('M/D/YYYY')}`;

  if (diff < 7) {
    rowWeekText += ` (${diff} day${diff > 1 ? 's' : ''})`;
  }
  return rowWeekText;
}

const getNumFromVal = (stringValue: string, isIntake: boolean) => {
  if (isNaN(parseInt(stringValue, 10))) {
    return 1000;
  }
  return isIntake ? parseInt(stringValue, 10) : parseFloat(stringValue);
};
